import { useEffect, useSyncExternalStore } from 'react';
import {
  isMarkColourKey,
  markKeys,
  type MarkColourKey,
  type MarkedAgent,
} from './agent-mark.util';

/**
 * Each of the org's agents' colour key, for the marks, so a mark anywhere
 * (the sidebar, a chat, a task) reads the same colour without loading the
 * agents itself. Two sources:
 *
 * - the colour stored on an agent, from every agent the agents API returns
 *   (the list, one agent, a new one, a copy): `rememberAgentMarkColours`;
 * - for an agent without one (made before colours were stored, until the
 *   backfill gives it one), its place in creation order, published by
 *   whatever loads the agents list: `publishAgentMarkOrder`.
 *
 * A stored colour never changes, so it wins and is kept once seen.
 */
let published: ReadonlyMap<string, MarkColourKey> = new Map();
let publishedSignature = '';
const stored = new Map<string, MarkColourKey>();
let keys: ReadonlyMap<string, MarkColourKey> | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function changed() {
  keys = new Map([...published, ...stored]);
  listeners.forEach((listener) => listener());
}

export function publishAgentMarkOrder(agents: readonly MarkedAgent[]): void {
  const next = markKeys(agents);
  const nextSignature = [...next].map(([id, key]) => id + ':' + key).join(',');
  if (nextSignature === publishedSignature) return;
  publishedSignature = nextSignature;
  published = next;
  changed();
}

/** Keeps each agent's stored colour; a null or unknown one is left out. */
export function rememberAgentMarkColours(
  agents: readonly Pick<MarkedAgent, 'id' | 'markColor'>[]
): void {
  let added = false;
  for (const { id, markColor } of agents) {
    if (!isMarkColourKey(markColor) || stored.get(id) === markColor) continue;
    stored.set(id, markColor);
    added = true;
  }
  if (added) changed();
}

/** Publishes a loaded agents list; nothing while it loads. */
export function usePublishAgentMarkOrder(
  agents: readonly MarkedAgent[] | undefined
): void {
  useEffect(() => {
    if (agents) publishAgentMarkOrder(agents);
  }, [agents]);
}

export function useAgentMarkKeys(): ReadonlyMap<string, MarkColourKey> | null {
  return useSyncExternalStore(
    subscribe,
    () => keys,
    () => keys
  );
}

/** Tests only: forget every key. */
export function resetAgentMarkOrder(): void {
  published = new Map();
  publishedSignature = '';
  stored.clear();
  keys = null;
  listeners.forEach((listener) => listener());
}
